import { Alert, Box, Checkbox, FormControlLabel, Snackbar, Switch, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { apiPaths, authHeader, handleResponse } from '../../../queryClient';
import { ApiProfile, Company, CompanySettings, Settings } from '../../../types';

const style = { borderBottom: theme => `1px solid ${theme.palette.divider}`, mb: 4, pb: 1 };

type SettingUpdate = {
  checked: boolean;
  name: string;
};

type SettingUpdateContext = {
  prevSettings: CompanySettings;
};

const ProfileSettings = ({ company }: { company: Company }) => {
  const queryClient = useQueryClient();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [disabled, setDisable] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);

  const { control, setValue } = useForm<any>({
    defaultValues: {
      settings: company?.settings,
    },
  });

  useEffect(() => {
    if (company) {
      setValue('settings', company?.settings);
    }
  }, [company]);

  const { mutate: onUpdate } = useMutation<unknown, Response, SettingUpdate, SettingUpdateContext>({
    async mutationFn({ checked, name }) {
      return fetch(`/api/companies/${company?.companyId}/update-settings`, {
        body: JSON.stringify({ [name]: checked }),
        headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
        method: 'PATCH',
      }).then(handleResponse);
    },
    onMutate({ checked, name }) {
      setValue(`settings.${name}`, checked);

      return { prevSettings: company?.settings };
    },
    onSuccess: (_, { checked, name }) => {
      queryClient.setQueriesData<ApiProfile>({ queryKey: [{ path: apiPaths.me }] }, profile => {
        if (!profile) {
          return;
        }

        return {
          ...profile,
          companies: profile.companies.map(profileCompany =>
            profileCompany.companyId === company.companyId
              ? {
                  ...profileCompany,
                  settings: {
                    ...profileCompany.settings,
                    [name]: checked,
                  },
                }
              : profileCompany,
          ),
        };
      });
      setDisable(false);
      setShowSuccessToast(true);
    },
    onError: (_, __, context) => {
      setDisable(false);
      setShowErrorToast(true);
      setValue('settings', context?.prevSettings ?? company.settings);
    },
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = event.target;

    onUpdate({ checked, name });
    setDisable(true);
  };

  return (
    <Box textAlign="left" test-id="ProfileSettingsComponent">
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        open={showSuccessToast}
      >
        <Alert onClose={() => setShowSuccessToast(false)} severity="success">
          Settings were updated successfully
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={3000}
        onClose={() => setShowErrorToast(false)}
        open={showErrorToast}
      >
        <Alert onClose={() => setShowErrorToast(false)} severity="error">
          Something went wrong
        </Alert>
      </Snackbar>

      {company?.isHomeCompany && (
        <Box sx={style}>
          <Box sx={{ justifyContent: 'space-between', gap: 1, display: 'flex', mb: 1 }}>
            <Typography variant="h6" mt={1}>
              Group simulations
            </Typography>
            <Controller
              name={`settings.${Settings.canViewGroupSimulations}`}
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  disabled={disabled}
                  onChange={handleChange}
                  name={Settings.canViewGroupSimulations}
                />
              )}
            />
          </Box>

          <Typography color="text.secondary" variant="body2">
            Allow creating and scheduling team (group) simulations, in addition to individual ones.
          </Typography>
        </Box>
      )}

      <Box sx={style}>
        <Box sx={{ justifyContent: 'space-between', gap: 1, display: 'flex', mb: 1 }}>
          <Typography variant="h6" mt={1}>
            System service updates (recommended)
          </Typography>
          <Controller
            name={`settings.${Settings.systemService}`}
            control={control}
            render={({ field }) => {
              return (
                <Switch
                  checked={field.value}
                  disabled={disabled}
                  onChange={handleChange}
                  name={Settings.systemService}
                />
              );
            }}
          />
        </Box>

        <Typography color="text.secondary" variant="body2">
          Get to know critical situations regarding the overall service.
        </Typography>
      </Box>

      <Box sx={style}>
        <Box sx={{ justifyContent: 'space-between', gap: 1, display: 'flex', mb: 1 }}>
          <Typography variant="h6" mt={1}>
            Operating improvements
          </Typography>
          <Controller
            name={`settings.${Settings.operational}`}
            control={control}
            render={({ field }) => {
              return (
                <Switch checked={field.value} disabled={disabled} onChange={handleChange} name={Settings.operational} />
              );
            }}
          />
        </Box>

        <Typography color="text.secondary" variant="body2">
          Notifications of updates and improvement affecting the experience by the user.
        </Typography>
      </Box>

      <Box sx={style}>
        <Box sx={{ justifyContent: 'space-between', gap: 1, display: 'flex', mb: 1 }}>
          <Typography variant="h6" mt={1}>
            General marketing and offers
          </Typography>
          <Controller
            name={`settings.${Settings.marketing}`}
            control={control}
            render={({ field }) => {
              return (
                <Switch checked={field.value} disabled={disabled} onChange={handleChange} name={Settings.marketing} />
              );
            }}
          />
        </Box>

        <Typography color="text.secondary" variant="body2">
          We keep you updated on the latest offers and new features.
        </Typography>
      </Box>

      <Box sx={style}>
        <Typography variant="h6" mb={2}>
          Areas of interest
        </Typography>
        <Typography color="text.secondary" variant="body2" gutterBottom>
          Limit your notifications to your relevant areas of interest. The K-Sim product range expands a wide area of
          application and some may be more relevant to you than others.
        </Typography>

        <Box marginLeft={1}>
          <Controller
            name={`settings.${Settings.kSimConnect}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    disabled={disabled}
                    name={Settings.kSimConnect}
                    onChange={handleChange}
                  />
                }
                label="K-Sim Connect"
              />
            )}
          />
        </Box>

        <Box marginLeft={1}>
          <Controller
            name={`settings.${Settings.kSimEngine}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    disabled={disabled}
                    name={Settings.kSimEngine}
                    onChange={handleChange}
                  />
                }
                label="K-Sim Engine"
              />
            )}
          />
        </Box>

        <Box marginLeft={1}>
          <Controller
            name={`settings.${Settings.kSimCargo}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    disabled={disabled}
                    name={Settings.kSimCargo}
                    onChange={handleChange}
                  />
                }
                label="K-Sim Cargo"
              />
            )}
          />
        </Box>

        <Box marginLeft={1}>
          <Controller
            name={`settings.${Settings.kSimNavigation}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    disabled={disabled}
                    name={Settings.kSimNavigation}
                    onChange={handleChange}
                  />
                }
                label="K-Sim Navigation"
              />
            )}
          />
        </Box>

        <Box marginLeft={1}>
          <Controller
            name={`settings.${Settings.kSimDP}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox checked={field.value} disabled={disabled} name={Settings.kSimDP} onChange={handleChange} />
                }
                label="K-Sim DP"
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileSettings;
